import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
} from '@angular/core'
import { MatDialog } from '@angular/material/dialog'
import {
  AppointmentModel,
  COLOR,
  getReferralStatusColor,
  getWorstReferralStatusText,
  Patient,
  PatientInsurance,
  PatientInsuranceType,
  Permission,
  ProgressIndicatorService,
  REFERRAL_STATUS,
  sortPatientInsurance,
} from '@mmx/shared'
import { TranslateService } from '@ngx-translate/core'
import { BehaviorSubject, filter, Subject, takeUntil } from 'rxjs'

import { ConfirmNecessityDialogComponent } from '../../confirm-necessity-dialog'

interface StatusState {
  tooltipText: string
  insuranceItems: {
    insurance: PatientInsurance
    status?: REFERRAL_STATUS
    statusColor?: COLOR
  }[]
  statuses: {
    primary?: COLOR
    secondary?: COLOR
  }
}

@Component({
  selector: 'mmx-referral-status-icon',
  templateUrl: 'referral-status-icon.component.html',
  styleUrls: ['./referral-status-icon.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ReferralStatusIconComponent implements OnChanges, OnDestroy {
  private onDestroy$ = new Subject<void>()
  public statusChanged = new EventEmitter<void>()

  @Input() patient?: Patient
  @Input() appointment: AppointmentModel
  @Input() statuses?: REFERRAL_STATUS[]
  @Output() referralSaved = new EventEmitter<void>()

  public inProgress = false
  public state$ = new BehaviorSubject<StatusState>(this.getUpdatedState())
  public readonly Permission = Permission
  public readonly REFERRAL_STATUS = REFERRAL_STATUS

  constructor(
    private snackService: ProgressIndicatorService,
    private dialog: MatDialog,
    private translate: TranslateService,
  ) {}

  public ngOnChanges() {
    this.update()
  }

  public ngOnDestroy(): void {
    this.onDestroy$.next()
    this.onDestroy$.complete()
  }

  public openConfirmDialog(
    insurance: PatientInsurance,
    status?: REFERRAL_STATUS,
  ): void {
    this.dialog
      .open(ConfirmNecessityDialogComponent, {
        panelClass: 'dialog-form',
        data: {
          type: 'referral',
          appointment: this.appointment,
          insurance,
          status,
        },
      })
      .afterClosed()
      .pipe(takeUntil(this.onDestroy$), filter(Boolean))
      .subscribe({
        next: () => {
          this.referralSaved.emit()
          this.snackService.snack({
            message: this.translate.instant('icons.referrals.updated'),
            duration: 5000,
          })
        },
        error: () => {
          this.snackService.snack({
            message: this.translate.instant('icons.referrals.error'),
            duration: 5000,
          })
        },
      })
  }

  private update(): void {
    this.state$.next(this.getUpdatedState())
  }

  private getUpdatedState(): StatusState {
    const referralStatuses = (this.appointment?.referrals || []).map(
      (r) => r.status,
    )
    const insuranceItems = this.getInsuranceItems()

    const state: StatusState = {
      insuranceItems,
      statuses: {
        primary: insuranceItems.find(
          (item) => item.insurance.type === PatientInsuranceType.PRIMARY,
        )?.statusColor,
        secondary: insuranceItems.find(
          (item) => item.insurance.type === PatientInsuranceType.SECONDARY,
        )?.statusColor,
      },
      tooltipText: getWorstReferralStatusText(referralStatuses),
    }

    return state
  }

  private getInsuranceItems() {
    const insurances = sortPatientInsurance(this.appointment?.insurances || [])
    return insurances.map((insurance) => {
      const referral = (this.appointment.referrals || []).find(
        (referral) => referral.insuranceId === insurance.id,
      )
      if (!referral) {
        return { insurance }
      }

      return {
        insurance,
        status: referral.status,
        statusColor: getReferralStatusColor(referral.status),
      }
    })
  }
}
